import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import MicIcon from "@mui/icons-material/Mic";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import StopIcon from "@mui/icons-material/Stop";
import type { SvgIconComponent } from "@mui/icons-material";
import dayjs from "dayjs";
import { colors } from "../../config";
import useAuthentication from "../../hooks/useAuthentication";
import usePopup from "../../hooks/usePopup";
import AiDrawerCredits from "../AiWallet/AiDrawerCredits";
import EmrAiChatThread, {
  type EmrChatMessage,
  type VoiceChatListeningState,
} from "../../EMR/components/EmrAiChatThread";
import {
  VOICE_AI_RESPONSE_EVENT,
  type VoiceAiResponseDetail,
} from "./voiceAssistantEvents";
import {
  VOICE_COMMAND_EVENT,
  dispatchVoiceListeningCycleEnd,
} from "../../context/VoiceAssistant/VoiceAssistantContext";
import { useAiChatSpeech } from "./useAiChatSpeech";
import { voicePromptService } from "../../services/voiceAssistantService";
import { isEmrRoute } from "../../utilis/methods/voiceAssistant/emrRoute";
import { useEmrUrlPatientIds } from "../../utilis/methods/voiceAssistant/useEmrUrlPatientIds";
import { dispatchEmrFetchPatientSummary } from "../../utilis/methods/voiceAssistant/emrPatientSummary";
import { useEmrPatientSummary } from "../../utilis/methods/voiceAssistant/useEmrPatientSummary";
import FieldSummaryModal from "./FieldSummaryModal";
import EmrPatientSuggestionModal from "../../EMR/components/layouts/EmrPatientSuggestionModal";
import {
  AI_CHAT_UI_COMMAND_EVENT,
  VOICE_CHAT_INPUT_EVENT,
  VOICE_CHAT_LISTENING_EVENT,
  dispatchVoiceChatMode,
  parseAiChatVoiceCommand,
  type AiChatVoiceUiAction,
  type VoiceChatListeningDetail,
} from "../../utilis/methods/voiceAssistant/aiChatVoiceCommands";
import type { ResponseType } from "../../Types/ServicesType/servicesType";
import type {
  VoicePromptRequest,
  VoicePromptResponseBody,
  VoicePromptType,
} from "../../Types/VoiceAssistant/VoiceAssistantTypes";
import {
  downloadBase64File,
  getPatientServiceFileMimeType,
  isPatientServiceFileResponse,
  resolvePatientServiceFile,
  type PatientServiceFileType,
} from "../../utilis/methods/voiceAssistant/resolvePatientServiceFile";

export type AiPromptType = "pdf" | "chat";

const CHAT_OPTION = { label: "Chat", value: "chat" as const };
const REPORTS_OPTION = { label: "Reports", value: "pdf" as const };

function chatContentFromData(data: unknown): string {
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    const msg = (data as { message?: string; reply?: string; content?: string })
      .message ??
      (data as { reply?: string }).reply ??
      (data as { content?: string }).content;
    if (typeof msg === "string" && msg.trim()) return msg.trim();
    return JSON.stringify(data, null, 2);
  }
  return "No response received.";
}

function normalizeResponseType(type: string | null | undefined): VoicePromptType {
  if (!type) return null;
  if (type === "field summary" || type === "field-summary") return "field-summary";
  if (type === "menu" || type === "pdf" || type === "translate" || type === "chat") {
    return type;
  }
  return null;
}

function parseApiResult(
  result: ResponseType<VoicePromptResponseBody>,
  fallbackType: VoicePromptType,
) {
  const body = result.response;
  if (!body) {
    return {
      type: fallbackType,
      declaredFileType: null as string | null,
      data: null as string | null,
    };
  }
  return {
    type: normalizeResponseType(body.type) ?? fallbackType,
    declaredFileType: body.type ?? null,
    data: typeof body.data === "string" ? body.data : null,
  };
}

function FilePreviewModal({
  base64,
  fileType,
  open,
  setOpen,
}: {
  base64: string;
  fileType: PatientServiceFileType;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const { CustomModal } = usePopup();
  const objectUrl = useMemo(() => {
    if (!base64 || fileType === "unknown") return null;
    try {
      const raw = base64.includes(",") ? base64.split(",")[1] : base64;
      const byteCharacters = atob(raw);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(null)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      return URL.createObjectURL(
        new Blob([byteArray], { type: getPatientServiceFileMimeType(fileType) }),
      );
    } catch {
      return null;
    }
  }, [base64, fileType]);

  useEffect(() => {
    if (!open || !base64) return;
    if (fileType === "xlsx" || fileType === "xls") {
      downloadBase64File(base64, fileType);
    }
  }, [base64, fileType, open]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const title =
    fileType === "pdf"
      ? "AI report"
      : fileType === "xlsx" || fileType === "xls"
        ? "AI Excel report"
        : "AI file";

  return (
    <CustomModal title={title} open={open} setOpen={setOpen}>
      <Box sx={{ width: "min(90vw, 900px)", height: fileType === "pdf" ? "75vh" : "auto", p: 2 }}>
        {fileType === "pdf" && objectUrl ? (
          <iframe
            title="AI report PDF"
            src={objectUrl}
            style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }}
          />
        ) : fileType === "image" && objectUrl ? (
          <Box
            component="img"
            src={objectUrl}
            alt="AI report"
            sx={{ maxWidth: "100%", maxHeight: "70vh", display: "block", mx: "auto" }}
          />
        ) : fileType === "xlsx" || fileType === "xls" ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your Excel report is ready. It has been downloaded automatically.
            </Typography>
            <Button
              variant="contained"
              onClick={() => downloadBase64File(base64, fileType)}
            >
              Download again
            </Button>
          </Box>
        ) : (
          <Typography color="error">Could not load file preview.</Typography>
        )}
      </Box>
    </CustomModal>
  );
}

const AI_ACTIVE_BORDER_GRADIENT = `conic-gradient(
  from 0deg,
  #6554C0 0deg,
  #5E4DB2 60deg,
  #0C66E4 120deg,
  #579DFF 180deg,
  #8777D9 240deg,
  #6554C0 300deg
)`;

type QuickAction = {
  id: string;
  title: string;
  description: string;
  icon: SvgIconComponent;
  accent: string;
  onClick: () => void;
};

type Props = {
  sidebarOpen: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

export default function AiPromptPanel({ sidebarOpen, onOpen, onClose }: Props) {
  const { pathname } = useLocation();
  const onEmrPage = isEmrRoute(pathname);
  const emrIds = useEmrUrlPatientIds();
  const { user } = useAuthentication();
  const { showSnackbar } = usePopup();
  const promptTypeOptions = useMemo(
    () => [CHAT_OPTION, REPORTS_OPTION],
    [],
  );
  const [promptType, setPromptType] = useState<AiPromptType>("chat");
  const [promptText, setPromptText] = useState("");
  const [chatMessages, setChatMessages] = useState<EmrChatMessage[]>([]);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileModalType, setFileModalType] =
    useState<PatientServiceFileType>("pdf");
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const {
    hasPatientContext,
    summaryData,
    modalOpen: summaryModalOpen,
    setModalOpen: setSummaryModalOpen,
    isLoading: summaryLoading,
    handleFetchSummary,
  } = useEmrPatientSummary();
  const [voiceListening, setVoiceListening] = useState<VoiceChatListeningState>({
    listening: false,
    previewText: "",
  });
  const pendingVoiceReplyRef = useRef(false);
  const voiceChatInFlightRef = useRef(false);
  const promptTypeRef = useRef(promptType);

  useEffect(() => {
    promptTypeRef.current = promptType;
  }, [promptType]);

  const voiceChatModeRef = useRef(false);
  const submitChatRef = useRef<(text: string, fromVoiceChat: boolean) => void>(
    () => {},
  );
  const speakReplyRef = useRef<(text: string) => Promise<void>>(async () => {});

  const resumeVoiceChatListening = useCallback(() => {
    voiceChatInFlightRef.current = false;
    if (voiceChatModeRef.current && promptTypeRef.current === "chat") {
      dispatchVoiceListeningCycleEnd();
    }
  }, []);

  const appendAssistantChat = useCallback((data: unknown, speakReply = false) => {
    const content = chatContentFromData(data);
    setChatMessages((prev) => [
      ...prev,
      { id: `a-${Date.now()}`, role: "assistant", content },
    ]);
    if (speakReply) {
      void speakReplyRef.current(content).finally(() => {
        resumeVoiceChatListening();
      });
      return;
    }
    if (voiceChatInFlightRef.current) {
      resumeVoiceChatListening();
    }
  }, [resumeVoiceChatListening]);

  const applyResponse = useCallback(
    (
      type: VoicePromptType,
      data: unknown,
      declaredFileType?: string | null,
    ) => {
      if (!sidebarOpen) {
        onOpen?.();
      }
      if (type === "chat") {
        setPromptType("chat");
        const speak = pendingVoiceReplyRef.current;
        pendingVoiceReplyRef.current = false;
        appendAssistantChat(data, speak);
        return;
      }
      if (
        typeof data === "string" &&
        data &&
        isPatientServiceFileResponse(type, declaredFileType)
      ) {
        const resolvedFileType = resolvePatientServiceFile(declaredFileType, data);
        if (resolvedFileType === "unknown") {
          showSnackbar({
            open: true,
            message: "Unsupported file type returned by patient service.",
            variant: "warning",
            duration: 5000,
          });
          return;
        }
        setFileBase64(data);
        setFileModalType(resolvedFileType);
        setFileModalOpen(true);
        return;
      }
      if (typeof data === "string" && data) {
        showSnackbar({
          open: true,
          message: data,
          variant: "info",
          duration: 6000,
        });
      }
    },
    [appendAssistantChat, onOpen, showSnackbar, sidebarOpen],
  );

  const { mutate: submitPrompt, isLoading } = useMutation(voicePromptService, {
    onSuccess: (result, variables) => {
      if (result.responseIndicator !== "success") {
        showSnackbar({
          open: true,
          message: result.responseMessage || "Request could not be processed.",
          variant: "warning",
          duration: 5000,
        });
        pendingVoiceReplyRef.current = false;
        if (voiceChatInFlightRef.current) {
          resumeVoiceChatListening();
        }
        return;
      }
      const { type, data, declaredFileType } = parseApiResult(result, variables.type);
      applyResponse(type, data, declaredFileType);
    },
    onError: (error: { message?: string; response?: { data?: { responseMessage?: string } } }) => {
      pendingVoiceReplyRef.current = false;
      showSnackbar({
        open: true,
        message:
          error?.response?.data?.responseMessage ||
          error?.message ||
          "AI request failed.",
        variant: "error",
        duration: 5000,
      });
      if (voiceChatInFlightRef.current) {
        resumeVoiceChatListening();
      }
    },
    onSettled: (_data, _error, variables) => {
      if (voiceChatInFlightRef.current) return;
      if (variables?.type === "chat" && voiceChatModeRef.current) return;
      dispatchVoiceListeningCycleEnd();
    },
  });

  const buildPayload = useCallback(
    (prompt: string, type: AiPromptType): VoicePromptRequest => {
      const base: VoicePromptRequest = {
        date: dayjs().format("YYYY-MM-DD"),
        prompt,
        type,
        language: navigator.language || null,
        userId: user?.userId ?? user?.login_id ?? null,
      };
      if (!emrIds.isOnEmr) return base;
      return {
        ...base,
        encounterId: emrIds.encounterId,
        patientMasterId: emrIds.patientMasterId,
        patientId: emrIds.patientId,
      };
    },
    [emrIds, user?.login_id, user?.userId],
  );

  const submitChatMessage = useCallback(
    (text: string, fromVoiceChat: boolean) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setChatMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: "user", content: trimmed },
      ]);
      setPromptText("");
      pendingVoiceReplyRef.current = fromVoiceChat;
      if (fromVoiceChat) {
        voiceChatInFlightRef.current = true;
      }
      submitPrompt(buildPayload(trimmed, "chat"));
    },
    [buildPayload, submitPrompt],
  );

  submitChatRef.current = submitChatMessage;

  const {
    voiceChatMode,
    setVoiceChatMode,
    isDictating,
    interimText,
    toggleDictation,
    stopDictation,
    speakAssistantReply,
    speakMessage,
    speechInputSupported,
    speechOutputSupported,
  } = useAiChatSpeech({
    onFinalTranscript: (text) => {
      if (promptTypeRef.current !== "chat") {
        setPromptText(text);
        return;
      }
      submitChatRef.current(text, voiceChatModeRef.current);
    },
    onError: (message) => {
      showSnackbar({
        open: true,
        message,
        variant: "warning",
        duration: 5000,
      });
    },
  });

  useEffect(() => {
    voiceChatModeRef.current = voiceChatMode;
    speakReplyRef.current = speakAssistantReply;
  }, [voiceChatMode, speakAssistantReply]);

  const handleSpeakMessage = useCallback(
    async (messageId: string, text: string) => {
      if (!speechOutputSupported || !text.trim()) return;
      setSpeakingMessageId(messageId);
      try {
        await speakMessage(text);
      } finally {
        setSpeakingMessageId(null);
      }
    },
    [speakMessage, speechOutputSupported],
  );

  const applyAiChatUiCommand = useCallback(
    (action: AiChatVoiceUiAction) => {
      setPromptType("chat");
      promptTypeRef.current = "chat";
      onOpen?.();
      stopDictation();
      if (action.enableVoiceChat) {
        setVoiceChatMode(true);
        voiceChatModeRef.current = true;
        dispatchVoiceChatMode(true);
        dispatchVoiceListeningCycleEnd();
      }
    },
    [onOpen, setVoiceChatMode, stopDictation],
  );

  useEffect(() => {
    const onUiCommand = (event: Event) => {
      const action = (event as CustomEvent<AiChatVoiceUiAction>).detail;
      if (!action) return;
      applyAiChatUiCommand(action);
    };

    window.addEventListener(AI_CHAT_UI_COMMAND_EVENT, onUiCommand);
    return () => window.removeEventListener(AI_CHAT_UI_COMMAND_EVENT, onUiCommand);
  }, [applyAiChatUiCommand]);

  useEffect(() => {
    dispatchVoiceChatMode(voiceChatMode);
    if (voiceChatMode) {
      stopDictation();
    } else {
      setVoiceListening({ listening: false, previewText: "" });
    }
  }, [voiceChatMode, stopDictation]);

  useEffect(() => {
    const onVoiceListening = (event: Event) => {
      const detail = (event as CustomEvent<VoiceChatListeningDetail>).detail;
      if (!detail) return;
      setVoiceListening({
        listening: detail.listening,
        previewText: detail.previewText ?? "",
      });
    };

    window.addEventListener(VOICE_CHAT_LISTENING_EVENT, onVoiceListening);
    return () =>
      window.removeEventListener(VOICE_CHAT_LISTENING_EVENT, onVoiceListening);
  }, []);

  useEffect(() => {
    if (promptType !== "chat" && voiceChatMode) {
      setVoiceChatMode(false);
      dispatchVoiceChatMode(false);
      stopDictation();
    }
  }, [promptType, voiceChatMode, setVoiceChatMode, stopDictation]);

  useEffect(() => {
    const submitVoiceChatCommand = (text: string) => {
      if (!voiceChatModeRef.current || promptTypeRef.current !== "chat") return;
      submitChatRef.current(text, true);
    };

    const onVoiceCommandCapture = (event: Event) => {
      const text = (event as CustomEvent<{ text: string }>).detail?.text?.trim();
      if (!text || parseAiChatVoiceCommand(text)) return;
      if (!voiceChatModeRef.current || promptTypeRef.current !== "chat") return;
      submitVoiceChatCommand(text);
      event.stopImmediatePropagation();
    };

    const onVoiceChatInput = (event: Event) => {
      const text = (event as CustomEvent<{ text: string }>).detail?.text?.trim();
      if (!text) return;
      submitVoiceChatCommand(text);
    };

    window.addEventListener(VOICE_COMMAND_EVENT, onVoiceCommandCapture, true);
    window.addEventListener(VOICE_CHAT_INPUT_EVENT, onVoiceChatInput);
    return () => {
      window.removeEventListener(VOICE_COMMAND_EVENT, onVoiceCommandCapture, true);
      window.removeEventListener(VOICE_CHAT_INPUT_EVENT, onVoiceChatInput);
    };
  }, []);

  const chatInputValue =
    isDictating && interimText
      ? `${promptText}${promptText ? " " : ""}${interimText}`
      : promptText;

  const handleSubmit = () => {
    const text = chatInputValue.trim();
    if (!text) return;
    if (promptType === "chat") {
      submitChatMessage(text, false);
      return;
    }
    setPromptText("");
    submitPrompt(buildPayload(text, promptType));
  };

  useEffect(() => {
    const onAiResponse = (event: Event) => {
      const { type, data, declaredFileType } = (
        event as CustomEvent<VoiceAiResponseDetail>
      ).detail;
      if (!isPatientServiceFileResponse(type, declaredFileType) && type !== "chat") {
        return;
      }
      applyResponse(type, data, declaredFileType);
    };

    const onVoiceCommand = (event: Event) => {
      if (!onEmrPage) return;
      const text = (event as CustomEvent<{ text: string }>).detail?.text?.trim();
      if (!text) return;
      if (voiceChatModeRef.current && promptTypeRef.current === "chat") return;
      if (/report|pdf/i.test(text)) {
        setPromptType("pdf");
        onOpen?.();
        submitPrompt(buildPayload(text, "pdf"));
        return;
      }
      if (/\bchat\b/i.test(text)) {
        setPromptType("chat");
        setChatMessages((prev) => [
          ...prev,
          { id: `u-${Date.now()}`, role: "user", content: text },
        ]);
        onOpen?.();
        submitPrompt(buildPayload(text, "chat"));
        return;
      }
      dispatchEmrFetchPatientSummary();
    };

    window.addEventListener(VOICE_AI_RESPONSE_EVENT, onAiResponse);
    window.addEventListener(VOICE_COMMAND_EVENT, onVoiceCommand);
    return () => {
      window.removeEventListener(VOICE_AI_RESPONSE_EVENT, onAiResponse);
      window.removeEventListener(VOICE_COMMAND_EVENT, onVoiceCommand);
    };
  }, [applyResponse, buildPayload, onEmrPage, onOpen, submitPrompt]);

  const userDisplayName = user?.emp_name || user?.username || "there";

  const handleOpenSuggestions = useCallback(() => {
    if (!hasPatientContext) {
      showSnackbar({
        open: true,
        message: "Patient encounter context is missing.",
        variant: "warning",
        duration: 4000,
      });
      return;
    }
    setSuggestionModalOpen(true);
  }, [hasPatientContext, showSnackbar]);

  const handleClearChat = useCallback(() => {
    setChatMessages([]);
    setPromptText("");
    setSpeakingMessageId(null);
  }, []);

  const canClearChat =
    promptType === "chat" &&
    (chatMessages.length > 0 || promptText.trim().length > 0) &&
    !isLoading &&
    !isDictating;

  const quickActions = useMemo<QuickAction[]>(() => [
    {
      id: "report",
      title: "Generate report",
      description: "Create a PDF report from your prompt",
      icon: DescriptionOutlinedIcon,
      accent: "#0ea5e9",
      onClick: () => {
        setPromptType("pdf");
        setPromptText("Generate a report about ");
      },
    },
    {
      id: "explain",
      title: "Chat with AI",
      description: "Break down complex topics clearly",
      icon: PsychologyOutlinedIcon,
      accent: "#14b8a6",
      onClick: () => {
        setPromptType("chat");
        setPromptText("Explain this in simple terms:\n\n");
      },
    },
  ], []);

  const showWelcome =
    promptType === "chat" &&
    chatMessages.length === 0 &&
    !isLoading &&
    !isDictating &&
    !(voiceChatMode && voiceListening.listening);

  const aiComposerActive = isLoading || isDictating;

  return (
    <>
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, #f8fafc 0%, #eef2f6 48%, #e3f2fd 100%)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            bgcolor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(10px)",
            borderBottom: "1px solid",
            borderColor: "rgba(148,163,184,0.25)",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${colors.primaryMain} 0%, ${colors.primaryDark} 100%)`,
              color: "#fff",
              boxShadow: "0 8px 20px rgba(33,150,243,0.28)",
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              AI assistant
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Ask anything about your workflow
            </Typography>
          </Box>
          {promptType === "chat" ? (
            <Tooltip title="Clear chat">
              <span>
                <IconButton
                  size="small"
                  aria-label="Clear chat"
                  onClick={handleClearChat}
                  disabled={!canClearChat}
                  sx={{
                    bgcolor: "rgba(148,163,184,0.12)",
                    "&:hover": { bgcolor: "rgba(148,163,184,0.2)" },
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {onClose ? (
            <IconButton
              size="small"
              aria-label="Close AI assistant"
              onClick={onClose}
              sx={{
                bgcolor: "rgba(148,163,184,0.12)",
                "&:hover": { bgcolor: "rgba(148,163,184,0.2)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>

        <AiDrawerCredits />

        {onEmrPage ? (
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
              bgcolor: "rgba(255,255,255,0.82)",
              borderBottom: "1px solid",
              borderColor: "rgba(148,163,184,0.25)",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={
                summaryLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SummarizeIcon />
                )
              }
              onClick={handleFetchSummary}
              disabled={summaryLoading}
              sx={{ flex: 1, minWidth: 0, textTransform: "none", fontWeight: 600 }}
            >
              Patient summary
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="success"
              startIcon={<LightbulbIcon />}
              onClick={handleOpenSuggestions}
              sx={{ flex: 1, minWidth: 0, textTransform: "none", fontWeight: 600 }}
            >
              AI suggestions
            </Button>
          </Box>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: showWelcome ? "center" : "flex-end",
            px: showWelcome ? 2.5 : 2,
            py: showWelcome ? 3 : 2,
          }}
        >
          {showWelcome ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: 520,
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                position: "relative",
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle, rgba(33,150,243,0.22) 0%, rgba(30,136,229,0.08) 45%, transparent 70%)",
                  filter: "blur(2px)",
                  pointerEvents: "none",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 0.75,
                  background: `linear-gradient(90deg, ${colors.primary800}, ${colors.primaryMain}, ${colors.primaryDark})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Welcome, {userDisplayName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 360, mb: 2.5, lineHeight: 1.6 }}
              >
                Start a conversation or pick a quick action below. Use voice chat
                or the mic when you want hands-free input.
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.25,
                }}
              >
                {quickActions.map((action) => {
                  const ActionIcon = action.icon;
                  return (
                    <Box
                      key={action.id}
                      component="button"
                      type="button"
                      onClick={action.onClick}
                      disabled={action.id === "patient-summary" && summaryLoading}
                      sx={{
                        textAlign: "left",
                        border: "1px solid",
                        borderColor: "rgba(148,163,184,0.28)",
                        borderRadius: "14px",
                        bgcolor: "rgba(255,255,255,0.88)",
                        p: 1.5,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          borderColor: action.accent,
                          boxShadow: `0 12px 28px ${action.accent}22`,
                        },
                        "&:disabled": {
                          opacity: 0.65,
                          cursor: "not-allowed",
                          transform: "none",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: `${action.accent}18`,
                          color: action.accent,
                          mb: 1,
                        }}
                      >
                        <ActionIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25 }}>
                        {action.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        {action.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : promptType === "chat" ? (
            chatMessages.length > 0 ||
            isLoading ||
            isDictating ||
            (voiceChatMode && voiceListening.listening) ? (
              <EmrAiChatThread
                messages={chatMessages}
                isLoading={isLoading}
                voiceListening={
                  voiceChatMode && voiceListening.listening && !isLoading
                    ? voiceListening
                    : undefined
                }
                speechOutputSupported={speechOutputSupported}
                speakingMessageId={speakingMessageId}
                onSpeakMessage={handleSpeakMessage}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 420,
                  mx: "auto",
                  textAlign: "center",
                  py: 4,
                }}
              >
                <ChatBubbleOutlineIcon
                  sx={{ fontSize: 40, color: "primary.main", mb: 1, opacity: 0.8 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Type a message below or choose a quick action to get started.
                </Typography>
              </Box>
            )
          ) : isLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 6,
              }}
            >
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Generating report…
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                mx: "auto",
                textAlign: "center",
                py: 4,
              }}
            >
              <DescriptionOutlinedIcon
                sx={{ fontSize: 40, color: "primary.main", mb: 1, opacity: 0.8 }}
              />
              <Typography variant="body2" color="text.secondary">
                Describe the report you need in the composer below. The PDF opens
                when it is ready.
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            width: "100%",
            flexShrink: 0,
            mt: "auto",
            px: 2,
            pt: 1.5,
            pb: 2,
            boxSizing: "border-box",
            bgcolor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid",
            borderColor: "rgba(148,163,184,0.25)",
          }}
        >
          {promptType === "chat" && speechInputSupported ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: 1,
                px: 0.5,
              }}
            >
              <Chip
                icon={<RecordVoiceOverIcon />}
                label="Voice chat"
                size="small"
                clickable
                color={voiceChatMode ? "primary" : "default"}
                variant={voiceChatMode ? "filled" : "outlined"}
                onClick={() => {
                  const next = !voiceChatMode;
                  if (next) {
                    setPromptType("chat");
                    promptTypeRef.current = "chat";
                  } else {
                    voiceChatInFlightRef.current = false;
                  }
                  setVoiceChatMode(next);
                  voiceChatModeRef.current = next;
                  dispatchVoiceChatMode(next);
                  stopDictation();
                  if (next) {
                    dispatchVoiceListeningCycleEnd();
                  }
                }}
                sx={{ bgcolor: voiceChatMode ? undefined : "rgba(255,255,255,0.9)" }}
              />
              {voiceChatMode ? (
                <Typography
                  variant="caption"
                  color={voiceListening.listening ? "primary.main" : "text.secondary"}
                  sx={{ fontWeight: voiceListening.listening ? 600 : 400 }}
                >
                  {voiceListening.listening
                    ? voiceListening.previewText
                      ? "Listening…"
                      : "Listening — speak now"
                    : "Speak using the voice assistant — drawer mic is off"}
                </Typography>
              ) : null}
              {voiceChatMode && speechOutputSupported ? (
                <Typography variant="caption" color="text.secondary">
                  · AI replies are read aloud
                </Typography>
              ) : null}
            </Box>
          ) : null}
          <Box
            sx={{
              position: "relative",
              borderRadius: "18px",
              ...(aiComposerActive
                ? {
                    filter: `
                      drop-shadow(0 0 6px rgba(94, 77, 178, 0.5))
                      drop-shadow(0 0 14px rgba(12, 102, 228, 0.35))
                      drop-shadow(0 0 22px rgba(94, 77, 178, 0.25))
                    `,
                  }
                : {
                    boxShadow: "0 14px 40px rgba(15,23,42,0.1)",
                  }),
            }}
          >
            <Box
              sx={{
                position: "relative",
                borderRadius: "18px",
                overflow: "hidden",
                border: aiComposerActive ? "none" : "1px solid",
                borderColor: "rgba(148,163,184,0.3)",
                ...(aiComposerActive
                  ? {
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: "200%",
                        aspectRatio: "1",
                        transform: "translate(-50%, -50%)",
                        background: AI_ACTIVE_BORDER_GRADIENT,
                        filter: "blur(18px)",
                        opacity: 0.65,
                        animation: "aiBorderGlowSpin 3s linear infinite",
                        zIndex: 0,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "inherit",
                        padding: "2px",
                        background: AI_ACTIVE_BORDER_GRADIENT,
                        WebkitMask:
                          "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        maskComposite: "exclude",
                        animation: "aiBorderRingSpin 3s linear infinite",
                        zIndex: 1,
                      },
                      "@keyframes aiBorderGlowSpin": {
                        from: {
                          transform: "translate(-50%, -50%) rotate(0deg)",
                        },
                        to: {
                          transform: "translate(-50%, -50%) rotate(360deg)",
                        },
                      },
                      "@keyframes aiBorderRingSpin": {
                        from: { transform: "rotate(0deg)" },
                        to: { transform: "rotate(360deg)" },
                      },
                    }
                  : {}),
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  borderRadius: "16px",
                  overflow: "hidden",
                  bgcolor: "background.paper",
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={8}
                  placeholder={
                    promptType === "chat"
                      ? voiceChatMode
                        ? "Use the voice assistant to send messages…"
                        : "Ask anything…"
                      : "Describe the report you need…"
                  }
                  value={chatInputValue}
                  onChange={(e) => setPromptText(e.target.value)}
                  variant="standard"
                  disabled={isLoading || isDictating}
                  InputProps={{ disableUnderline: true }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      alignItems: "flex-start",
                      px: 2,
                      pt: 1.75,
                      pb: 0.75,
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.9rem",
                      lineHeight: 1.55,
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "text.disabled",
                      opacity: 1,
                    },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1.1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "rgba(248,250,252,0.9)",
                  }}
                >
                  <FormControl size="small" sx={{ flexShrink: 0 }}>
                    <Select
                      id="global-ai-type-select"
                      value={promptType}
                      disabled={isLoading}
                      onChange={(e) => setPromptType(e.target.value as AiPromptType)}
                      sx={{
                        minWidth: 120,
                        fontSize: "0.8125rem",
                        borderRadius: "999px",
                        bgcolor: "background.paper",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(148,163,184,0.35)",
                        },
                      }}
                    >
                      {promptTypeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {promptType === "chat" && speechInputSupported ? (
                    <Tooltip
                      title={
                        voiceChatMode
                          ? "Voice chat uses the voice assistant — turn off Voice chat to use this mic"
                          : isDictating
                            ? "Stop dictation"
                            : "Voice to text"
                      }
                    >
                      <IconButton
                        aria-label="Voice input"
                        size="small"
                        disabled={isLoading || voiceChatMode}
                        onClick={() => {
                          if (!voiceChatMode) {
                            toggleDictation();
                          }
                        }}
                        sx={{
                          bgcolor: isDictating ? "error.main" : "rgba(148,163,184,0.14)",
                          color: isDictating ? "error.contrastText" : "text.primary",
                          "&:hover": {
                            bgcolor: isDictating ? "error.dark" : "rgba(148,163,184,0.22)",
                          },
                        }}
                      >
                        {isDictating ? (
                          <StopIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <MicIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <IconButton
                    aria-label="Send prompt"
                    size="small"
                    disabled={isLoading || isDictating || !chatInputValue.trim()}
                    onClick={handleSubmit}
                    sx={{
                      width: 36,
                      height: 36,
                      ml: "auto",
                      background: `linear-gradient(135deg, ${colors.primaryMain} 0%, ${colors.primaryDark} 100%)`,
                      color: "#fff",
                      boxShadow: "0 8px 18px rgba(33,150,243,0.35)",
                      "&:hover": {
                        background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primary800} 100%)`,
                      },
                      "&.Mui-disabled": {
                        background: "action.disabledBackground",
                        color: "action.disabled",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mt: 0.75, px: 1.5, fontSize: "0.7rem" }}
          >
            {promptType === "chat"
              ? voiceChatMode
                ? "Voice chat: speak with the voice assistant — sends when you stop talking"
                : "Mic: speak and your message sends automatically when you stop"
              : "Enter to send · Shift+Enter for new line"}
          </Typography>
        </Box>
      </Box>

      <FilePreviewModal
        base64={fileBase64 || ""}
        fileType={fileModalType}
        open={fileModalOpen}
        setOpen={setFileModalOpen}
      />
      <FieldSummaryModal
        open={summaryModalOpen}
        data={summaryData}
        onClose={() => setSummaryModalOpen(false)}
      />
      <EmrPatientSuggestionModal
        open={suggestionModalOpen}
        onClose={() => setSuggestionModalOpen(false)}
      />
    </>
  );
}
